/**
 * The Library's ONE delete confirmation — an in-app dialog, never a native
 * `window.confirm` (invisible to headless browsers and screen readers, and it
 * looked like an unrelated browser warning; #677 / #679).
 *
 * Library LB6-T2 (L6-18): the table's row and bulk Delete, the sidebar's page
 * Delete and the article editor's Delete all open THIS dialog, with the
 * intent `buildDeleteIntent` / `buildArticleDeleteIntent` describes — so an
 * article deleted from the sidebar is confirmed exactly as one deleted from
 * the table.
 */
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { DeleteIntent } from './LibraryDocumentsView.misc-ddd-cluster';

export interface DeleteConfirmDialogProps {
  /** What is being deleted; null = closed. */
  intent: DeleteIntent | null;
  /** Closed without deleting. */
  onCancel: () => void;
  /** Confirmed: the caller deletes. */
  onConfirm: (intent: DeleteIntent) => void;
}

export function DeleteConfirmDialog({ intent, onCancel, onConfirm }: DeleteConfirmDialogProps) {
  return (
    <AlertDialog
      open={intent !== null}
      onOpenChange={(open) => { if (!open) onCancel(); }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {intent?.title ?? 'Delete?'}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {intent?.body ?? 'This action cannot be undone.'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>
            {intent?.cancelLabel ?? 'Cancel'}
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="confirm-delete"
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={() => {
              if (intent) onConfirm(intent);
            }}
          >
            {intent?.confirmLabel ?? 'Delete'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
